import { cn } from "@/lib/utils";
import { useState } from "react";

import { getFormationBannerUrl, getFormationLogotypeUrl } from "./smart-catalog.utils";

type Props = {
    /**
     * Codes produit à essayer dans l'ordre. Quand toutes les images du
     * premier code échouent, on tente le suivant. Si tous échouent, un
     * carré neutre de la même taille est rendu (pas de null).
     */
    productCodes: (string | null | undefined)[];
    alt: string;
    className?: string;
};

/** Vignette 100×100 px — bannière en fond, logotype par-dessus. */
export function FormationThumbnail({ productCodes, alt, className }: Props) {
    const [candidateIndex, setCandidateIndex] = useState(0);
    const [bannerHidden, setBannerHidden] = useState(false);
    const [logotypeHidden, setLogotypeHidden] = useState(false);

    const productCode = productCodes[candidateIndex] ?? null;
    const bannerUrl = getFormationBannerUrl(productCode);
    const logotypeUrl = getFormationLogotypeUrl(productCode);

    const bannerVisible = Boolean(bannerUrl && !bannerHidden);
    const logotypeVisible = Boolean(logotypeUrl && !logotypeHidden);

    const tryNextCandidate = () => {
        const next = candidateIndex + 1;
        if (next < productCodes.length) {
            setCandidateIndex(next);
            setBannerHidden(false);
            setLogotypeHidden(false);
        } else {
            setBannerHidden(true);
            setLogotypeHidden(true);
        }
    };

    const handleBannerError = () => {
        if (logotypeVisible) {
            setBannerHidden(true);
        } else {
            tryNextCandidate();
        }
    };

    const handleLogotypeError = () => {
        if (bannerVisible) {
            setLogotypeHidden(true);
        } else {
            tryNextCandidate();
        }
    };

    const noImages = (!bannerUrl || bannerHidden) && (!logotypeUrl || logotypeHidden);

    return (
        <div className={cn("relative size-[100px] shrink-0 overflow-hidden rounded-md bg-gray-100", className)}>
            {!noImages ? (
                <>
                    {bannerUrl && !bannerHidden ? (
                        <img src={bannerUrl} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" onError={handleBannerError} />
                    ) : null}
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-gray-900/20" aria-hidden />
                    {logotypeUrl && !logotypeHidden ? (
                        <img src={logotypeUrl} alt={alt} className="absolute inset-0 m-auto h-[85%] w-[85%] object-contain p-1" onError={handleLogotypeError} />
                    ) : null}
                </>
            ) : null}
        </div>
    );
}
